/**
 * The pages the models read most that never name the brand (SYR-111 v4).
 *
 * On the "what you need to know today" tier because it is the finding a
 * CMO can act on without reading a table: the models keep going to these
 * pages, and the brand is not on them.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { Tooltip } from 'antd';
import { Card, Eyebrow } from '../../atoms';
import { THEME } from '../../config/theme';
import { IModelBeliefsPageFinding } from '../../../../services/nestjs-base-api';
import { usePageKindLabel } from './pageKind';

interface Props {
  pages: IModelBeliefsPageFinding[];
  brandName: string;
}

export const NeverNamedPages: React.FunctionComponent<Props> = ({ pages, brandName }) => {
  const intl = useIntl();
  const kindLabel = usePageKindLabel();
  if (pages.length === 0) return null;
  const answers = pages.reduce((sum, p) => sum + p.answers, 0);

  return (
    <Card>
      <Eyebrow style={{ marginBottom: 6 }}>
        {intl.formatMessage({
          id: 'marketerView.modelBeliefs.neverNamed.eyebrow',
          defaultMessage: 'Where you are missing',
        })}
      </Eyebrow>
      <div style={{ fontSize: 17, fontWeight: 700, color: THEME.textBase, marginBottom: 6, lineHeight: 1.3 }}>
        {intl.formatMessage(
          {
            id: 'marketerView.modelBeliefs.neverNamed.title',
            defaultMessage:
              '{count, plural, one {The models keep reading one page that never names {brand}} other {The models keep reading {count} pages that never name {brand}}}',
          },
          { count: pages.length, brand: brandName },
        )}
      </div>
      <div style={{ fontSize: 13.5, color: THEME.textSecondary, marginBottom: 12, maxWidth: '72ch', lineHeight: 1.5 }}>
        {intl.formatMessage(
          {
            id: 'marketerView.modelBeliefs.neverNamed.body',
            defaultMessage:
              'Between them these pages were read in {answers} of the answers the models gave with web search, and you are not on any of them. Getting named there is the most direct way to change what the models recommend. The number is how many answers read each page.',
          },
          { answers },
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {pages.map((p) => (
          <Tooltip
            key={p.domain}
            title={
              p.pages > 1
                ? intl.formatMessage(
                    {
                      id: 'marketerView.modelBeliefs.neverNamed.sitePages',
                      defaultMessage: '{kind}, {pages} pages of this site',
                    },
                    { kind: kindLabel(p.kind), pages: p.pages },
                  )
                : `${kindLabel(p.kind)} · ${p.domain}`
            }
          >
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 13,
                color: THEME.textBase,
                background: THEME.bgChip,
                border: `1px solid ${THEME.border}`,
                borderRadius: 999,
                padding: '4px 12px',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 260,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {p.title ?? p.domain}
              </span>
              {p.title ? <span style={{ color: THEME.textMuted }}> · {p.domain}</span> : null} · {p.answers}
            </a>
          </Tooltip>
        ))}
      </div>
    </Card>
  );
};
